import { memo, useCallback, useEffect, useState } from 'react'
import { ModalShell } from '../common/ModalShell.js'
import { playoutApi, type GapFillOptions, type LiveFillEntry } from '../../lib/api.js'
import {
  LiveFillCatalog,
  LiveFillSelection,
  campaignPickLabel,
  fmtPreset,
  fmtSec,
  useLiveFillSelection,
  KIND_LABELS,
} from '../monitor/LiveFillComposer.js'
import './AgendaEditors.css'
import '../monitor/LiveSkipDialog.css'
import { templateFillEntries } from '../../lib/liveBreakTemplate.js'

/**
 * Préparation du décrochage préfait d'une journée d'antenne.
 *
 * Se compose exactement comme un comblage de régie, avec le même catalogue : ce
 * qui est préparé ici est ce que « Décrocher (modèle) » posera en un clic pendant
 * l'émission, sans que l'opérateur ait à choisir quoi que ce soit en direct.
 *
 * La clé est la journée d'antenne et non la grille : les grilles de diffusion
 * sont hebdomadaires, alors que les spots disponibles changent chaque jour.
 */

const WINDOW_PRESETS_SEC = [60, 120, 300, 600]

interface LiveBreakTemplateDialogProps {
  channelId: string
  /** Journée d'antenne au format `AAAA-MM-JJ`. */
  day:       string
  onClose:   () => void
}

export const LiveBreakTemplateDialog = memo(function LiveBreakTemplateDialog({
  channelId, day, onClose,
}: LiveBreakTemplateDialogProps) {
  const [options, setOptions] = useState<GapFillOptions | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [notice,  setNotice]  = useState('')
  const [mode,      setMode]      = useState<'FIXED' | 'OPEN'>('FIXED')
  const [windowSec, setWindowSec] = useState(120)

  const fill = useLiveFillSelection(options)

  // Catalogue et modèle déjà enregistré, chargés ensemble : le modèle est décrit
  // par des identifiants, il faut le catalogue pour lui redonner ses libellés.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      playoutApi.getGapFillOptions(channelId, day),
      playoutApi.getLiveBreakTemplate(channelId, day),
    ])
      .then(([catalog, template]) => {
        if (cancelled) return
        setOptions(catalog)
        setMode(template.mode)
        if (template.windowSec) setWindowSec(template.windowSec)
        fill.setSelected(restoreItems(templateFillEntries(template.items), catalog))
      })
      .catch(() => { if (!cancelled) setError('Impossible de charger le contenu disponible') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // `fill.setSelected` est stable (useState) : la dépendance complète
    // relancerait le chargement à chaque frappe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId, day])

  const handleSave = useCallback(async () => {
    setSaving(true); setError(''); setNotice('')
    try {
      await playoutApi.saveLiveBreakTemplate(channelId, day, {
        mode,
        windowSec: mode === 'FIXED' ? windowSec : null,
        items:     fill.entries as LiveFillEntry[],
      })
      setNotice('Décrochage préfait enregistré pour cette journée')
    } catch (e: any) {
      setError(e?.message ?? 'Enregistrement refusé')
    } finally {
      setSaving(false)
    }
  }, [channelId, day, mode, windowSec, fill.entries])

  const handleClear = useCallback(async () => {
    setSaving(true); setError(''); setNotice('')
    try {
      await playoutApi.deleteLiveBreakTemplate(channelId, day)
      fill.setSelected([])
      setNotice('Décrochage préfait retiré')
    } catch (e: any) {
      setError(e?.message ?? 'Suppression refusée')
    } finally {
      setSaving(false)
    }
  }, [channelId, day, fill])

  // Journée d'antenne en clair : « samedi 26 juillet 2026 », pas « 2026-07-26 ».
  const dayLabel = new Date(`${day}T12:00:00`).toLocaleDateString('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  // Un clic sur l'infoservice comble tout le reste (`lib/liveBreakTemplate.ts`) :
  // avec une durée donnée, il en prend ce qui reste après pubs et jingles.
  const { hasElastic, addInfoservice, addInfoserviceRest } = fill
  const handleAddInfoservice = useCallback((durationSec: number) => {
    if (hasElastic) addInfoservice(durationSec)
    else addInfoserviceRest()
  }, [hasElastic, addInfoservice, addInfoserviceRest])

  // Jingle de la chaîne posé à l'aller (sauf si le modèle commence par un
  // jingle) et au retour (sauf s'il finit par un jingle) : tous deux dans la
  // durée donnée, miroir de `planLiveBreak`. Ils réduisent d'autant la part de
  // l'infoservice.
  const channelJingle = options?.resumeJingle ?? null
  const windowCapSec  = mode === 'FIXED' ? windowSec : 0
  const openingDueSec = channelJingle && fill.selected[0]?.kind !== 'JINGLE' ? Math.ceil(channelJingle.durationSec) : 0
  const openingSec    = mode === 'OPEN' || Math.ceil(fill.fixedSec) + openingDueSec <= windowCapSec ? openingDueSec : 0
  const returnDueSec  = mode === 'FIXED' && channelJingle && fill.selected[fill.selected.length - 1]?.kind !== 'JINGLE'
    ? Math.ceil(channelJingle.durationSec)
    : 0
  const returnSec     = Math.ceil(fill.fixedSec) + openingSec + returnDueSec <= windowCapSec ? returnDueSec : 0

  const elasticSec = Math.max(0, windowCapSec - fill.fixedSec - openingSec - returnSec)
  const overfill = mode === 'FIXED' && fill.fixedSec > windowSec
  // Durée réellement posée : l'infoservice qui comble le reste compte pour ce
  // qu'il occupe, jingles de la chaîne compris. Le seul total des éléments à
  // durée fixe affichait 1m04s sur 2m00s pour un décrochage plein.
  const composedSec = fill.hasElastic ? windowCapSec : fill.fixedSec + openingSec + returnSec

  return (
    <ModalShell
      className="agenda-editor gap-fill live-skip"
      closeDisabled={saving}
      onClose={onClose}
      size="wide"
      title={`Décrochage préfait — ${dayLabel}`}
    >
      <div className="gap-fill__body">
        <div className="gap-fill__summary">
          <div>
            <div className="gap-fill__summary-copy">
              Ce contenu sera posé d'un seul clic pendant le direct, par
              « Décrocher (modèle) ». Rien n'est écrit en grille maintenant.
            </div>
          </div>
          <div className={`gap-fill__status is-${overfill ? 'danger' : 'info'}`}>
            <div className="gap-fill__status-value">
              {mode === 'OPEN' && fill.hasElastic ? 'Libre' : fmtSec(composedSec)}
            </div>
            <div className="gap-fill__status-detail">
              {mode === 'OPEN' ? 'sans durée fixe' : `sur ${fmtSec(windowSec)}`}
            </div>
          </div>
        </div>

        <div className="gap-fill__layout live-skip__layout">
          <div className="gap-fill__selection-column">
            <div className="gap-fill__preview">
              <div className="agenda-editor__eyebrow">Contenu du décrochage</div>

              {openingSec > 0 && channelJingle && (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Départ</span>
                  <span className="gap-fill__boundary-label">{channelJingle.title}</span>
                  <span className="gap-fill__boundary-time">{fmtSec(openingSec)}</span>
                </div>
              )}

              <LiveFillSelection
                selected={fill.selected}
                elasticSec={elasticSec}
                disabled={saving}
                emptyLabel="Aucun décrochage préparé pour cette journée"
                onMove={fill.moveItem}
                onRemove={fill.removeItem}
              />

              {returnSec > 0 && channelJingle && (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Retour</span>
                  <span className="gap-fill__boundary-label">{channelJingle.title}</span>
                  <span className="gap-fill__boundary-time">{fmtSec(returnSec)}</span>
                </div>
              )}

              <div className="live-break__duration">
                <span className="live-break__duration-label" id="live-break-template-duration">
                  Durée du décrochage
                </span>
                <div className="live-break__presets" role="group" aria-labelledby="live-break-template-duration">
                  {WINDOW_PRESETS_SEC.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      className="tp-button tp-button--ghost live-break__preset"
                      data-active={(mode === 'FIXED' && windowSec === preset) || undefined}
                      disabled={saving || mode === 'OPEN'}
                      onClick={() => { setMode('FIXED'); setWindowSec(preset) }}
                    >
                      {fmtPreset(preset)}
                    </button>
                  ))}
                  <span className="live-break__free" data-disabled={(saving || mode === 'OPEN') || undefined}>
                    <input
                      type="number" min={5} step={1}
                      className="live-break__seconds"
                      value={windowSec}
                      disabled={saving || mode === 'OPEN'}
                      onChange={event => setWindowSec(Math.max(5, Number(event.target.value) || 0))}
                      aria-label="Durée du décrochage en secondes"
                    />
                    <span className="live-break__unit">s</span>
                  </span>
                </div>
              </div>

              <label className="live-skip__compact">
                <input
                  type="checkbox" checked={mode === 'OPEN'} disabled={saving}
                  onChange={event => setMode(event.target.checked ? 'OPEN' : 'FIXED')}
                />
                <span>
                  Sans durée fixe — raccrochage à la main
                  <span className="live-skip__compact-hint">
                    Exige un infoservice « combler tout le reste » : c'est lui qui tient l'antenne
                    jusqu'à ce que l'opérateur raccroche.
                  </span>
                </span>
              </label>
            </div>

            {error && <div className="agenda-editor__notice is-danger" role="alert">⚠ {error}</div>}
            {notice && <div className="agenda-editor__notice is-success" role="status">✓ {notice}</div>}

            <div className="agenda-editor__actions live-skip__actions">
              <button type="button" className="tp-button tp-button--ghost" onClick={onClose} disabled={saving}>
                Fermer
              </button>
              <button
                type="button" className="tp-button tp-button--outline-warning"
                onClick={() => { void handleClear() }}
                disabled={saving || fill.selected.length === 0}
              >
                Retirer le modèle
              </button>
              <button
                type="button" className="tp-button tp-button--primary"
                onClick={() => { void handleSave() }}
                disabled={saving || overfill || (mode === 'OPEN' && !fill.hasElastic)}
              >
                {saving ? 'Enregistrement…' : '✓ Enregistrer'}
              </button>
            </div>
          </div>

          <LiveFillCatalog
            options={options}
            loading={loading}
            disabled={saving}
            hasElastic={fill.hasElastic}
            remainingSec={mode === 'OPEN' ? 1 : Math.max(0, windowSec - fill.fixedSec - openingSec - returnSec)}
            onAddSpot={fill.addSpot}
            onAddCampaign={fill.addCampaign}
            onAddInfoservice={handleAddInfoservice}
            onAddInfoserviceRest={fill.addInfoserviceRest}
            onAddProgram={fill.addProgram}
            onAddClip={fill.addClip}
          />
        </div>
      </div>
    </ModalShell>
  )
})

/**
 * Redonne aux entrées enregistrées leurs libellés, en les rapprochant du
 * catalogue du jour. Une entrée dont le contenu a disparu (spot désactivé,
 * jingle supprimé) est montrée telle quelle plutôt que masquée : l'opérateur doit
 * voir qu'il y a un trou dans son modèle.
 */
function restoreItems(entries: LiveFillEntry[], catalog: GapFillOptions) {
  const campaigns = catalog.campaigns ?? []
  const jingles   = catalog.jingles ?? []
  const clips     = catalog.clips ?? []

  return entries.map((entry, index) => {
    const spot = entry.spotId
      ? campaigns.flatMap(c => c.spots.map(s => ({ ...s, campaignName: c.name })))
        .find(s => s.id === entry.spotId)
      : null
    const campaign = entry.campaignId ? campaigns.find(c => c.id === entry.campaignId) ?? null : null
    const program = entry.programId ? jingles.find(j => j.id === entry.programId) : null
    const clip    = entry.clipId ? clips.find(c => c.id === entry.clipId) : null

    const label = spot?.title
      ?? campaign?.name
      ?? program?.title
      ?? clip?.name
      ?? (entry.itemType === 'INFOSERVICE' ? catalog.infoservice?.title ?? 'Infoservice' : 'Contenu introuvable')

    // Une campagne ne nomme aucun spot : son sous-titre dit la règle de tirage,
    // c'est elle qui sera rejouée à chaque décrochage.
    const sublabel = entry.fillRest
      ? 'Infoservice — comble le reste'
      : entry.itemType === 'AD_CAMPAIGN'
        ? `Campagne · ${campaignPickLabel(entry.pick ?? 'LEAST_AIRED', entry.spotCount ?? 1)}`
        : KIND_LABELS[entry.itemType]

    return {
      uid:         `tpl_${index}`,
      label,
      sublabel,
      durationSec: entry.durationSec ?? spot?.durationSec ?? program?.durationSec ?? clip?.durationSec ?? 0,
      kind:        entry.itemType,
      fillRest:    entry.fillRest,
      entry,
    }
  })
}
